import {
  browserWorkerPost,
  type BrowserWorkerHint,
} from '../api/keywordsApi';
import { crawlGoogleKeywords } from './googleSuggestCrawler';
import { fetchGoogleSuggestions } from './googleSuggestService';
import type { CrawlProgress, CrawlSettings, KeywordData } from '../types/keyword.types';
import { getClientInstanceKey, getClientSessionKey } from '../utils/sessionKey';

const WORKER_ID_KEY = 'keywordyab_worker_id';
/** Poll interval while waiting to claim own job (not idle volunteer polling). */
const WAITING_POLL_MS = 1500;

interface PollTask {
  id: string;
  seed: string;
  language_code: string;
  location_code: string;
  max_depth: number;
  max_nodes: number;
  max_concurrency: number;
  max_keywords: number;
  delay_ms: number;
  enable_alpha: boolean;
  enable_nested: boolean;
  enable_questions: boolean;
}

interface PollRunResponse {
  action: 'run';
  worker_id: string;
  task: PollTask;
  lease_token: string;
}

interface PollWaitResponse {
  action: 'wait';
  worker_id: string;
}

type PollResponse = PollRunResponse | PollWaitResponse;

interface HitCacheQueryBlock {
  enabled: boolean;
  hit?: boolean;
  suggestions?: string[];
}

interface ProgressResponse {
  hit_cache_query: HitCacheQueryBlock;
}

interface PendingStore {
  query: string;
  suggestions: string[];
}

export interface ClaimOwnJobOptions {
  jobId: string;
  hint: BrowserWorkerHint;
  /** Wall-clock start of the search (for escalate timeout). */
  searchStartedAt: number;
  onEscalate?: () => void;
  signal?: AbortSignal;
}

function taskToSettings(task: PollTask): CrawlSettings {
  return {
    maxDepth: task.max_depth,
    maxNodes: task.max_nodes,
    maxConcurrency: task.max_concurrency,
    maxKeywords: task.max_keywords,
    delayMs: task.delay_ms,
    enableAlpha: task.enable_alpha,
    enableNested: task.enable_nested,
    enableQuestions: task.enable_questions,
  };
}

function loadWorkerId(): string | null {
  try {
    return localStorage.getItem(WORKER_ID_KEY);
  } catch {
    return null;
  }
}

function saveWorkerId(id: string): void {
  try {
    localStorage.setItem(WORKER_ID_KEY, id);
  } catch {
    /* ignore */
  }
}

function clearWorkerId(): void {
  try {
    localStorage.removeItem(WORKER_ID_KEY);
  } catch {
    /* ignore */
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

type StoppedReason =
  | 'completed'
  | 'max_keywords'
  | 'max_nodes'
  | 'max_depth'
  | 'failed';

export class BrowserWorkerService {
  private claimAbort: AbortController | null = null;
  private activeTaskAbort: AbortController | null = null;
  private activeTask: { id: string; leaseToken: string } | null = null;

  /** True while polling to claim the user's own job (not idle volunteer mode). */
  isClaimPolling(): boolean {
    return this.claimAbort !== null;
  }

  /** True while executing a claimed crawl in this tab. */
  isExecutingTask(): boolean {
    return this.activeTaskAbort !== null;
  }

  /**
   * Poll browser-worker only until this tab claims its own job or escalate timeout.
   * No background idle polling for other users' jobs.
   */
  claimOwnJob(options: ClaimOwnJobOptions): void {
    const { jobId, hint, searchStartedAt, onEscalate, signal } = options;
    if (!hint.enabled) return;

    this.stopClaimPoll();

    const claimController = new AbortController();
    this.claimAbort = claimController;

    const onParentAbort = () => claimController.abort();
    signal?.addEventListener('abort', onParentAbort, { once: true });

    void this.claimPollLoop({
      jobId,
      escalateAfterSec: hint.escalateAfterSec,
      searchStartedAt,
      onEscalate,
      signal: claimController.signal,
    }).finally(() => {
      signal?.removeEventListener('abort', onParentAbort);
      if (this.claimAbort === claimController) {
        this.claimAbort = null;
      }
    });
  }

  /** Stop waiting-to-claim polls only (does not cancel an in-flight crawl). */
  stopClaimPoll(): void {
    this.claimAbort?.abort();
    this.claimAbort = null;
  }

  /** Full stop: claim poll + cancel active crawl (new search / unmount). */
  stop(): void {
    this.stopClaimPoll();
    const active = this.activeTask;
    this.activeTask = null;
    this.activeTaskAbort?.abort();
    this.activeTaskAbort = null;
    if (active) {
      void browserWorkerPost(`/tasks/${active.id}/fail`, {
        lease_token: active.leaseToken,
        error_message: 'cancelled by user',
      }).catch(() => undefined);
    }
  }

  private escalateElapsed(
    searchStartedAt: number,
    escalateAfterSec: number,
  ): boolean {
    return (
      escalateAfterSec > 0 &&
      Date.now() - searchStartedAt >= escalateAfterSec * 1000
    );
  }

  private async claimPollLoop(params: {
    jobId: string;
    escalateAfterSec: number;
    searchStartedAt: number;
    onEscalate?: () => void;
    signal: AbortSignal;
  }): Promise<void> {
    const { jobId, escalateAfterSec, searchStartedAt, onEscalate, signal } =
      params;

    while (!signal.aborted) {
      if (this.escalateElapsed(searchStartedAt, escalateAfterSec)) {
        onEscalate?.();
        return;
      }

      try {
        const res = await this.pollOnce();
        if (signal.aborted) return;

        if (res.action === 'run') {
          if (res.task.id === jobId) {
            await this.runTask(res);
            return;
          }
          // Job دیگری claim شد — lease را آزاد کن تا worker در busy نماند
          await browserWorkerPost(`/tasks/${res.task.id}/fail`, {
            lease_token: res.lease_token,
            error_message: 'released: not target job',
          }).catch(() => undefined);
        }
      } catch (err) {
        if (signal.aborted) return;
        console.error('browser worker claim poll failed:', err);
      }

      if (this.escalateElapsed(searchStartedAt, escalateAfterSec)) {
        onEscalate?.();
        return;
      }

      try {
        await sleep(WAITING_POLL_MS, signal);
      } catch {
        return;
      }
    }
  }

  private async pollOnce(): Promise<PollResponse> {
    const body: Record<string, unknown> = {
      client_instance_key: getClientInstanceKey(),
      client_session_key: getClientSessionKey(),
      status: 'idle',
    };
    const workerId = loadWorkerId();
    if (workerId) body.worker_id = workerId;

    try {
      const res = await browserWorkerPost<PollResponse>('/poll', body);
      if (res.worker_id) saveWorkerId(res.worker_id);
      return res;
    } catch (err) {
      // worker_id در localStorage بعد از حذف ادمین منقضی می‌شود
      if (
        workerId &&
        err instanceof Error &&
        /404|worker یافت نشد/i.test(err.message)
      ) {
        clearWorkerId();
        delete body.worker_id;
        const res = await browserWorkerPost<PollResponse>('/poll', body);
        if (res.worker_id) saveWorkerId(res.worker_id);
        return res;
      }
      throw err;
    }
  }

  private async runTask(res: PollRunResponse): Promise<void> {
    if (this.activeTaskAbort) return;

    const controller = new AbortController();
    this.activeTaskAbort = controller;
    const { task, lease_token: leaseToken } = res;
    this.activeTask = { id: task.id, leaseToken };
    const settings = taskToSettings(task);
    const startedAt = Date.now();

    let pendingStore: PendingStore | null = null;
    const keywordBatch: KeywordData[] = [];
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let lastProgress: CrawlProgress = {
      phase: 'alpha',
      requestsDone: 0,
      keywordsFound: 0,
    };

    const flushKeywords = async () => {
      if (!keywordBatch.length) return;
      const batch = keywordBatch.splice(0, keywordBatch.length);
      await browserWorkerPost(`/tasks/${task.id}/keywords`, {
        lease_token: leaseToken,
        keywords: batch.map((k) => ({
          keyword: k.keyword,
          parent_keyword: k.parent_keyword ?? '',
          depth: k.depth ?? 0,
          len: k.len ?? 1,
          source: k.source ?? 'google',
        })),
      });
    };

    const reportProgress = async (progress: CrawlProgress) => {
      const payload: Record<string, unknown> = {
        lease_token: leaseToken,
        phase: progress.phase,
        requests_done: progress.requestsDone,
        keywords_found: progress.keywordsFound,
        current_query: progress.currentQuery,
      };
      if (pendingStore) {
        payload.store_hit_cache_query = {
          query: pendingStore.query,
          suggestions: pendingStore.suggestions,
          http_status: 200,
        };
        pendingStore = null;
      }
      return browserWorkerPost<ProgressResponse>(
        `/tasks/${task.id}/progress`,
        payload,
      ).then((res) => res ?? { hit_cache_query: { enabled: false } });
    };

    const fetchSuggestions = async (
      query: string,
      progress: CrawlProgress,
    ): Promise<string[]> => {
      const cacheRes = await reportProgress(progress);
      const block = cacheRes.hit_cache_query;
      if (block?.enabled && block.hit && block.suggestions) {
        return block.suggestions;
      }
      const suggestions = await fetchGoogleSuggestions(query);
      pendingStore = { query, suggestions };
      await flushKeywords();
      return suggestions;
    };

    heartbeatTimer = setInterval(() => {
      void browserWorkerPost(`/tasks/${task.id}/heartbeat`, {
        lease_token: leaseToken,
      }).catch(console.error);
    }, 10_000);

    try {
      await crawlGoogleKeywords({
        seed: task.seed,
        language: task.language_code,
        country: task.location_code,
        settings,
        signal: controller.signal,
        fetchSuggestions,
        onProgress: (p) => {
          lastProgress = p;
          void reportProgress(p).catch(console.error);
        },
        onKeywordFound: (row) => {
          keywordBatch.push(row);
          if (keywordBatch.length >= 25) {
            void flushKeywords().catch(console.error);
          }
        },
      });

      await flushKeywords();

      const stoppedReason: StoppedReason =
        lastProgress.keywordsFound >= settings.maxKeywords
          ? 'max_keywords'
          : lastProgress.requestsDone >= settings.maxNodes
            ? 'max_nodes'
            : 'completed';

      await browserWorkerPost(`/tasks/${task.id}/complete`, {
        lease_token: leaseToken,
        total_keywords: lastProgress.keywordsFound,
        total_requests: lastProgress.requestsDone,
        stopped_reason: stoppedReason,
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
      });
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error('browser worker task failed:', err);
        await browserWorkerPost(`/tasks/${task.id}/fail`, {
          lease_token: leaseToken,
          error_message: err instanceof Error ? err.message : 'task failed',
        }).catch((failErr) => {
          console.error('browser worker fail report failed:', failErr);
        });
      }
    } finally {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      this.activeTask = null;
      this.activeTaskAbort = null;
    }
  }
}

export const browserWorkerService = new BrowserWorkerService();
